






















































































































































































































































import Vue from "vue";
import { coordinates, Coordinates, reCoordInput, galaxies, Hop, Platform, dijkstraCalculator, DijkstraCalculator } from "common";
import { List } from "immutable";
import { blackholes } from "../utility/blackholes";
import { inputGalaxies, GalaxyTuple } from "../utility/generated";
import GalaxyMap from "./GalaxyMap.vue";
import RouteSummary from "./RouteSummary.vue";
import { ITimeEstimate } from "./RouteSummary.vue";
import { Explanation, toJourney, IEndPoint } from "../utility/explanation";

interface IFormData {
    startVal: string;
    destVal: string;
    galaxy: string;
    platform: string;
    maxJump: string;
    optimization: string;
}

interface IRouteSubmit {
    platform: string;
    galaxy: string;
    maxJump: number;
    start: Coordinates;
    dest: Coordinates;
}

interface IMessage {
    key: number;
    type: string;
    text: string;
}

export default Vue.extend({
    components: { GalaxyMap, RouteSummary },

    data(): {
        graphCount: number;
        coordPattern: string;
        galaxies: GalaxyTuple[];
        bhs: Coordinates[];
        exs: Coordinates[];
        formData: any;
        route: IRouteSubmit | null;
        journey: Explanation | null;
        timeEstimate: ITimeEstimate | null;
        messages: IMessage[];
        messageKey: number;
        windowWidth: number;
    } {
        return {
            graphCount: 0,
            coordPattern: reCoordInput,
            galaxies: inputGalaxies,
            bhs: [] as Coordinates[],
            exs: [] as Coordinates[],
            formData: {
                startVal: "",
                destVal: "",
                galaxy: "01 Euclid",
                platform: "ps4",
                maxJump: "2000",
            },
            route: null,
            journey: null,
            timeEstimate: null,
            messages: [],
            messageKey: 0,
            windowWidth: -1,
        };
    },

    watch: {
        formData: {
            handler() {
                const prev = this.getFormData();

                window.localStorage.setItem("TravelRequestForm_FormData", JSON.stringify(this.formData));

                if (prev !== null) {
                    if (prev!.galaxy !== this.formData.galaxy || prev!.platform !== this.formData.platform) {
                        this.updateHopData();
                        this.journey = null;
                        this.messages = [];
                    }
                }
            },
            deep: true,
        },

        route() {
            if (this.route !== null) {
                this.calculateTrip(this.route);
            }
        },
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.windowResizeEvent);
    },

    mounted() {
        console.log("TravelRequestForm mounted");

        this.windowWidth = window.innerWidth;
        window.addEventListener("resize", this.windowResizeEvent);

        const formData = this.getFormData();
        if (formData) {
            this.formData = formData;
        }

        this.updateHopData();
    },

    methods: {
        // toggleShowCoordinates() {
        //     this.showCoordinates = !this.showCoordinates;
        // },

        isOdd(v: number): boolean {
            return v % 2 !== 0;
        },

        getFormData(): IFormData | null {
            if (window.localStorage.getItem("TravelRequestForm_FormData")) {
                return JSON.parse(window.localStorage.getItem("TravelRequestForm_FormData")!);
            } else {
                return null;
            }
        },

        updateHopData(): void {
            const formData = this.getFormData();
            if (formData !== null) {
                if (formData! && formData!.galaxy.trim().length > 0 && formData.platform.trim().length > 0) {
                    const localHops = blackholes().then(bhs =>
                        bhs
                            .filter(hop => {
                                return hop.galaxy === formData!.galaxy;
                            })
                            .filter(hop => {
                                function tranlateHopPlatform(): number {
                                    if (hop.platform === "PC") {
                                        return 1;
                                    } else if (hop.platform === "PS4") {
                                        return 2;
                                    } else {
                                        throw new RangeError(hop.platform);
                                    }
                                }

                                function translateInputPlatform(): number {
                                    if (formData!.platform === "xbox") {
                                        return 1;
                                    } else if (formData!.platform === "ps4") {
                                        return 2;
                                    } else {
                                        throw new RangeError(formData!.platform);
                                    }
                                }

                                return tranlateHopPlatform() === translateInputPlatform();
                            })
                    );
                    localHops.then(hops => {
                        this.bhs = hops.map(h => h.blackhole.coords).toArray();
                        this.exs = hops.map(h => h.exit.coords).toArray();
                        this.graphCount += 1;
                    });
                }
            }
        },

        formatCoordinates() {
            function fc(coor: string): string | null {
                try {
                    const c = coordinates(coor);
                    return c.toString();
                } catch (e) {
                    return null;
                }
            }

            this.formData.startVal = fc(this.formData.startVal) || this.formData.startVal;
            this.formData.destVal = fc(this.formData.destVal) || this.formData.destVal;
        },

        submitForm() {
            this.graphCount += 1;

            this.formatCoordinates();
            this.messages = [];

            this.route = {
                platform: this.formData.platform,
                galaxy: this.formData.galaxy,
                maxJump: parseInt(this.formData.maxJump, 10),
                start: coordinates(this.formData.startVal),
                dest: coordinates(this.formData.destVal),
            };
        },

        async calculateTrip(route: IRouteSubmit) {
            this.journey = null;

            if (route.start.dist2Center() * 400 < 3000) {
                this.messages.unshift({
                    key: this.messageKey++,
                    type: "warning",
                    text: "Start appears to be invalid. That point is in the void at the center of the galaxy.",
                });
                return;
            }

            if (route.dest.dist2Center() * 400 < 3000) {
                this.messages.unshift({
                    key: this.messageKey++,
                    type: "warning",
                    text: "Destination appears to be invalid. That point is in the void at the center of the galaxy.",
                });
                return;
            }

            const startDist = Math.floor(route.start.dist2Center() * 400);
            const destDist = Math.floor(route.dest.dist2Center() * 400);
            const delta = Math.abs(startDist - (destDist + 20000));

            if (delta >= 30000) {
                this.messages.unshift({
                    key: this.messageKey++,
                    type: "warning",
                    text:
                        `Start is ${startDist.toLocaleString()} LY from center. ` +
                        `Destination is ${destDist.toLocaleString()} LY from center. ` +
                        `For best results, find a start location that is ` +
                        `just a little further away from center than the destination.`,
                });
            }

            const platformFilter: (hop: Hop) => boolean = (function() {
                if (route.platform == "ps4") {
                    return (hop: Hop) => {
                        return hop.platform === Platform.PS4;
                    };
                } else {
                    return (hop: Hop) => {
                        return hop.platform === Platform.PC;
                    };
                }
            })();

            const allHops: Hop[] = (await blackholes())
                .filter(platformFilter)
                .filter(hop => hop.galaxy === route.galaxy)
                .toArray();

            const shortest = dijkstraCalculator(allHops, route.maxJump, "time").findRoute([{ label: "start", coords: route.start }], {
                label: "destination",
                coords: route.dest,
            })[0];

            this.journey = new Explanation(route.maxJump, toJourney(List(shortest.route)));
            console.log(this.journey);

            for (const leg of this.journey.legs()) {
                console.log(leg.description);
            }

            const direct = dijkstraCalculator([], route.maxJump, "time").findRoute([{ label: "start", coords: route.start }], {
                label: "destination",
                coords: route.dest,
            })[0];

            const MinutesPerPoint = 62 / 38;

            this.timeEstimate = { route: MinutesPerPoint * shortest.score, direct: MinutesPerPoint * direct.score };
        },

        windowResizeEvent(ev: UIEvent) {
            this.windowWidth = window.innerWidth;
        },
    },
});
